import { PropsWithChildren, ReactNode, UIEventHandler, useRef } from 'react';
import styled, { css } from 'styled-components';
import { hideScrollbars, getFocusRingSpreadCSS } from '@ardoq/style-helpers';
import { FlexBox } from '@ardoq/layout';
import { Icon, IconName } from '@ardoq/icons';
import { colors, s4 } from '@ardoq/design-tokens';
import { useChevronSliders, useResizeObserver } from '@ardoq/hooks';

type Side = 'left' | 'right';

const getGradient = (side: Side) => `
linear-gradient(
  ${side === 'left' ? '90deg' : '270deg'},
  ${colors.bgDefault} 0%,
  ${colors.bgDefault} 60%,
  rgba(255, 255, 255, 0) 100%
)
`;

const ChipsListChildrenWrapper = styled(FlexBox).attrs({ gap: 'small' })<{
  onScroll?: UIEventHandler<HTMLDivElement>;
}>`
  overflow: auto;
  align-items: center;
  ${hideScrollbars};
  ${getFocusRingSpreadCSS}
`;

const CHEVRON_WIDTH = '60px';

/**
 * Chevrons are used to scroll the chips list when it overflows.
 * They are only shown when there's overflow in the respective direction.
 *
 * When a keyboard user Tabs through the chips, the browser automatically scrolls
 * the focused element into view
 * Therefore the chevrons become irrelevant for keyboard navigation and are hidden from assistive technologies.
 *
 */
const ChevronNav = styled.div.attrs({ 'aria-hidden': true })<{
  $side: Side;
  $isHidden: boolean;
}>`
  background: rgb(255, 255, 255);
  width: ${CHEVRON_WIDTH};
  padding: 0 ${s4};

  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  height: 100%;
  color: ${colors.iconAction};
  cursor: pointer;
  transition:
    left 0.2s,
    right 0.2s;
  &:hover {
    color: ${colors.iconActionHover};
  }

  ${({ $side, $isHidden }) =>
    $side === 'left'
      ? css`
          justify-content: start;
          background: ${getGradient($side)};
          left: ${$isHidden ? `-${CHEVRON_WIDTH}` : 0};
        `
      : css`
          justify-content: end;
          background: ${getGradient($side)};
          right: ${$isHidden ? `-${CHEVRON_WIDTH}` : 0};
        `}
`;

const ChipsListWrapperBase = styled(FlexBox).attrs({ gap: 'small' })`
  ${getFocusRingSpreadCSS};
`;

const ChipsListWithChevronWrapper = styled(ChipsListWrapperBase)`
  position: relative;
  overflow: hidden;
`;

const ChipsListWithWrap = styled(ChipsListWrapperBase)`
  flex-wrap: wrap;
`;

type ChipsListWithChevronsProps = {
  children?: ReactNode;
  'aria-label': string;
  role: 'toolbar' | 'list';
};

const ChipsListWithChevrons = ({
  children,
  role,
  'aria-label': ariaLabel,
}: ChipsListWithChevronsProps) => {
  const rootWrapperRef = useRef(null);
  const { width: rootWrapperWidth = 0 } = useResizeObserver(rootWrapperRef);
  const {
    wrapperRef,
    wrapperOnScroll,
    isLeftChevronVisible,
    isRightChevronVisible,
    onLeftChevronClick,
    onRightChevronClick,
  } = useChevronSliders(rootWrapperWidth, children);

  return (
    <ChipsListWithChevronWrapper ref={rootWrapperRef}>
      <ChevronNav
        $side="left"
        $isHidden={!isLeftChevronVisible}
        onClick={onLeftChevronClick}
        data-test-id={'chevron-nav-left'}
      >
        <Icon iconName={IconName.CHEVRON_LEFT} />
      </ChevronNav>
      <ChipsListChildrenWrapper
        ref={wrapperRef}
        onScroll={wrapperOnScroll}
        role={role}
        aria-label={ariaLabel}
      >
        {children}
      </ChipsListChildrenWrapper>
      <ChevronNav
        $side="right"
        $isHidden={!isRightChevronVisible}
        onClick={onRightChevronClick}
        data-test-id={'chevron-nav-right'}
      >
        <Icon iconName={IconName.CHEVRON_RIGHT} />
      </ChevronNav>
    </ChipsListWithChevronWrapper>
  );
};

type ChipsListProps = {
  overflowType?: 'chevrons' | 'wrap';
  'aria-label': string;
  role: 'list' | 'toolbar';
};

export const ChipsList = ({
  children,
  overflowType = 'chevrons',
  'aria-label': ariaLabel,
  role,
}: PropsWithChildren<ChipsListProps>) => {
  const Wrapper =
    overflowType === 'chevrons' ? ChipsListWithChevrons : ChipsListWithWrap;
  return (
    <Wrapper role={role} aria-label={ariaLabel}>
      {children}
    </Wrapper>
  );
};

export const CHIPS_LIST_SELECTOR = ChipsListWrapperBase.toString();
